import { db, type CacheRow } from './localDb.js'
import { enqueue } from './outbox.js'
import { uuidv7 } from './ids.js'
import { syncNow } from './syncEngine.js'
import { api } from './apiClient.js'
import { addPhoto, progressFor } from './photoQueue.js'
import { prepareImage } from './images.js'
import type { Actor } from './records.js'

/**
 * Rapporte vom Gerät aus.
 *
 * Der Rapport ist Klasse A und geht in den Ausgangskorb: er muss im Keller
 * ohne Empfang funktionieren, und er ist sofort quittiert. Die Kennung erzeugt
 * der Client, damit Fotos sich schon daran hängen können, bevor der Server den
 * Rapport je gesehen hat.
 *
 * Die Unterschrift ist Klasse B. Sie friert den Rapport ein, und das ist eine
 * Aussage über einen Beleg – dafür wartet man auf den Server. Ohne Netz wird
 * sie deshalb gar nicht erst angeboten.
 */

export interface RapportLineInput {
  kind: 'stunden' | 'material'
  catalogItemId?: string | null
  description: string
  quantity: number
  unit: string
  isExtra: boolean
}

export interface RapportInput {
  projectId: string
  workDate: string
  description: string
  isRegie: boolean
  lines: RapportLineInput[]
}

/**
 * Einen Rapport anlegen.
 *
 * Der lokale Cache bekommt ihn sofort mit `pending: true`. Ohne das wäre der
 * Rapport nach dem Speichern verschwunden, bis der Abgleich lief – im Keller
 * also nie.
 */
export async function createRapport(actor: Actor, input: RapportInput): Promise<string> {
  const id = uuidv7()
  const payload = {
    id,
    ...input,
    lines: input.lines.map((l, pos) => ({ ...l, pos }))
  }

  await db.rapports.put({
    ...payload,
    userId: actor.userId,
    status: 'eingereicht',
    signed: false,
    version: 1,
    changeSeq: 0,
    pending: true
  } as CacheRow)

  await enqueue({
    type: 'rapport.create',
    actionClass: 'A',
    method: 'POST',
    path: '/rapports',
    payload,
    tenantId: actor.tenantId,
    userId: actor.userId
  })
  void syncNow()
  return id
}

export async function updateRapport(
  actor: Actor,
  id: string,
  input: RapportInput,
  expectedVersion?: number
): Promise<void> {
  const existing = await db.rapports.get(id)

  /*
    Die erwartete Fassung kommt aus dem lokalen Stand, nicht aus dem Aufrufer.

    Sie war im Formular hart auf `1` verdrahtet. Beim ersten Speichern stimmte
    das zufällig; ab der zweiten Änderung stand der Rapport auf Fassung 2, der
    Server verglich mit 1 und antwortete mit VERSION_CONFLICT. Der Eintrag
    landete als Konflikt im Ausgangskorb, «Nochmal» half nicht – es schickte
    wieder die 1 –, und der Monteur konnte einen Rapport genau einmal ändern.

    Der Aufrufer darf weiterhin eine Fassung mitgeben, wenn er eine kennt.
    Kennt er keine, gilt der lokale Stand, und ohne lokalen Stand die 1.
  */
  const lokaleFassung = (existing as unknown as { version?: unknown } | undefined)?.version
  const fassung =
    expectedVersion ?? (typeof lokaleFassung === 'number' && lokaleFassung > 0 ? lokaleFassung : 1)

  const payload = {
    id,
    ...input,
    lines: input.lines.map((l, pos) => ({ ...l, pos })),
    expectedVersion: fassung
  }
  if (existing) await db.rapports.put({ ...existing, ...payload, pending: true })

  await enqueue({
    type: 'rapport.update',
    actionClass: 'A',
    method: 'PATCH',
    path: `/rapports/${id}`,
    payload,
    tenantId: actor.tenantId,
    userId: actor.userId
  })
  void syncNow()
}

/**
 * Unterschreiben – Klasse B, mit Absicht.
 *
 * Eine Unterschrift optimistisch zu quittieren, hiesse: der Monteur lässt den
 * Kunden unterschreiben, das Gerät sagt "erledigt", und eine Stunde später
 * weist der Server ab, weil der Rapport inzwischen verrechnet ist. Dann steht
 * der Monteur mit einer Unterschrift da, die es nicht gibt.
 */
export async function signRapport(
  _actor: Actor,
  id: string,
  input: { signedByName: string; signedPlace?: string; signature: string }
): Promise<{ locked: boolean }> {
  const result = await api.send<{ rapport: Record<string, unknown>; locked: boolean }>(
    `/rapports/${id}/signature`,
    'POST',
    input,
    uuidv7(),
    60_000
  )
  const vorhanden = await db.rapports.get(id)
  if (vorhanden) {
    await db.rapports.put({ ...vorhanden, ...result.rapport, signed: true, pending: false })
  }
  void syncNow()
  return { locked: result.locked }
}

/**
 * Ein Foto aufnehmen: sofort verkleinern, lokal ablegen, später übertragen.
 *
 * Verkleinert wird hier und nicht beim Hochladen. Sonst lägen zehn
 * Vier-Megabyte-Bilder im Gerätespeicher – und Safari räumt die irgendwann
 * weg, mitsamt dem Ausgangskorb daneben.
 */
export async function attachPhoto(
  actor: Actor,
  rapportId: string,
  file: Blob
): Promise<{ bytes: number; sourceBytes: number; rotated: boolean }> {
  const bild = await prepareImage(file)
  await addPhoto({
    tenantId: actor.tenantId,
    ownerType: 'rapport',
    ownerId: rapportId,
    blob: bild.blob,
    width: bild.width,
    height: bild.height
  })
  return { bytes: bild.blob.size, sourceBytes: bild.sourceBytes, rotated: bild.rotated }
}

export async function photoProgress(rapportId: string) {
  return progressFor(rapportId)
}

/**
 * Gesperrt heisst: unterschrieben oder verrechnet.
 *
 * Dieselbe Regel wie auf dem Server, bewusst an beiden Stellen. Die Oberfläche
 * darf einen gesperrten Rapport gar nicht erst zum Bearbeiten öffnen – und der
 * Server verlässt sich trotzdem nicht darauf.
 */
export function istGesperrt(rapport: CacheRow | null | undefined): boolean {
  if (!rapport) return false
  return rapport.signed === true || rapport.status === 'verrechnet'
}

export function sperrGrund(rapport: CacheRow): string {
  if (rapport.signed === true) {
    return 'Unterschrieben – dieser Rapport ist ein Beleg und lässt sich nicht mehr ändern.'
  }
  return 'Bereits verrechnet – dieser Rapport lässt sich nicht mehr ändern.'
}
